import React, {useState, useEffect} from "react";
import {Link} from "react-router-dom";
import {ArrowRight, Mail} from "lucide-react";
import {FaAmazon} from "react-icons/fa";
import Hero from "../components/Hero";
import axios from "axios";
import handplants from "../assets/typing.webp";

interface Book {
  id: number;
  title: string;
  author: string;
  price: number;
  cover_image_url?: string;
  synopsis?: string;
  status?: string;
  preorder_price?: number;
  amazon_url?: string | null;
  release_date?: string | null;
  progress_percentage?: number;
}

const Projects: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  // Removed modal flow in favor of inline Buy button on cards
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [newsletterName, setNewsletterName] = useState("");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState("");

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get("/api/books");
      setBooks(response.data.books || []);
    } catch (error) {
      console.error("Error fetching books:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookClick = (_book: Book) => {
    // No-op: clicking is handled by the flip/Buy button now
  };

  // deprecated: add-to-cart via modal removed

  // Removed old scrollToNewsletter handler in favor of a direct hash Link

  const handleNewsletterSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    setIsSubscribing(true);
    setSubscribeMessage("");
    try {
      const response = await axios.post("/api/newsletter/subscribe", {
        email: newsletterEmail,
        name: newsletterName,
      });
      setSubscribeMessage(response.data.message || "Subscribed successfully");
      setNewsletterEmail("");
      setNewsletterName("");
    } catch (error: any) {
      setSubscribeMessage(
        error?.response?.data?.error || "Failed to subscribe"
      );
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className={`${newsletterOpen ? "space-y-0" : "space-y-16"}`}>
      {/* Hero Section */}
      <Hero showShopButton={true} />

      {/* Hallie's Works Section */}
      <section
        className="py-16 bg-cover bg-center bg-no-repeat relative"
        style={{
          backgroundImage: `url(${handplants})`,
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-primary-100 opacity-80"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-end">
            <div className="bg-primary-300 border-4 border-primary-600 px-8 py-12 shadow-lg max-w-md">
              <h2 className="text-4xl font-bold text-primary-400 mb-6">
                Hallie's Works
              </h2>
              <p className="text-lg text-primary-400 mb-8 leading-relaxed">
                Step into the author's world. Sign up for the newsletter for
                exclusive first looks.
              </p>

              <div className="flex gap-4">
                <Link
                  to="/shop"
                  className="btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4 flex-1"
                >
                  Buy Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => setNewsletterOpen(true)}
                  className="btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4 flex-1"
                >
                  Newsletter
                  <Mail className="ml-2 h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Books Grid Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-primary-400 mb-12 text-center">
            Current & Upcoming Releases
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-lg text-primary-800">Loading books...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {books.map((book) => (
                <BookCard key={book.id} book={book} onClick={handleBookClick} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Modal */}
      {newsletterOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 m-0"
          onClick={() => setNewsletterOpen(false)}
        >
          <div
            className="relative bg-white rounded-lg shadow-xl w-full max-w-xl overflow-hidden mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setNewsletterOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
              aria-label="Close"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="p-8 md:p-10">
              <div className="bg-primary-300 border-4 border-primary-600 px-8 py-12 shadow-lg">
                <h2 className="text-4xl font-bold text-primary-400 mb-6 text-center">
                  Join the newsletter
                </h2>
                <p className="text-lg text-primary-800 mb-8 leading-relaxed text-center">
                  Be the first to read about new releases and exclusive
                  previews.
                </p>

                <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="proj-news-name"
                      className="block text-sm font-medium text-primary-800 mb-2"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="proj-news-name"
                      name="name"
                      value={newsletterName}
                      onChange={(e) => setNewsletterName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-primary-600 bg-white text-gray-900 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="proj-news-email"
                      className="block text-sm font-medium text-primary-800 mb-2"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="proj-news-email"
                      name="email"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-primary-600 bg-white text-gray-900 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="your@email.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubscribing}
                    className="w-full btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                  >
                    {isSubscribing ? "Subscribing..." : "Subscribe"}
                  </button>
                </form>

                {subscribeMessage && (
                  <p
                    className={`mt-4 text-sm ${
                      subscribeMessage.includes("Failed") ||
                      subscribeMessage.includes("already")
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    {subscribeMessage}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Book Card Component with flip animation
const BookCard: React.FC<{book: Book; onClick: (book: Book) => void}> = ({
  book,
  onClick,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "bg-primary-600 text-primary-50";
      case "preorder":
        return "bg-primary-400 text-primary-800";
      case "coming_soon":
        return "bg-primary-200 text-primary-800";
      default:
        return "bg-primary-700 text-primary-50";
    }
  };

  const getStatusText = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "Available";
      case "preorder":
        return "Preorder";
      case "coming_soon":
        return "Coming Soon";
      default:
        return "TBA";
    }
  };

  return (
    <div
      className="relative mx-auto cursor-pointer w-full max-w-xs aspect-[2/3] sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      onClick={() => onClick(book)}
    >
      <div
        className={`absolute inset-0 transition-transform duration-700 transform-style-preserve-3d ${
          isFlipped ? "rotate-y-180" : ""
        }`}
      >
        {/* Front of card - Book cover */}
        <div className="absolute inset-0 backface-hidden">
          <div className="w-full h-full bg-primary-300 border-4 border-primary-600 shadow-lg overflow-hidden">
            {book.cover_image_url ? (
              <img
                src={book.cover_image_url}
                alt={book.title}
                className="w-full h-full object-cover drop-shadow-lg"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary-800">
                <div className="text-center">
                  <div className="text-4xl mb-2">📚</div>
                  <p className="text-lg font-semibold">{book.title}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Back of card - Synopsis + CTA */}
        <div className="absolute inset-0 backface-hidden rotate-y-180">
          <div className="w-full h-full bg-primary-300 border-4 border-primary-600 shadow-lg p-6 flex flex-col">
            <h3 className="text-xl font-bold text-primary-400 mb-4 line-clamp-2">
              {book.title}
            </h3>

            <div className="flex-1">
              <p className="text-sm text-primary-800 mb-4 line-clamp-4">
                {book.synopsis || "Synopsis coming soon..."}
              </p>
            </div>

            <div className="mt-auto space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-800">
                  Status:
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                    book.status
                  )}`}
                >
                  {getStatusText(book.status)}
                </span>
              </div>
              {book.status?.toLowerCase() === "preorder" &&
                book.release_date && (
                  <p className="text-sm text-primary-800">
                    Releases {new Date(book.release_date).toLocaleDateString()}
                  </p>
                )}

              {/* Progress bar - only show for non-available books with progress data */}
              {book.status?.toLowerCase() !== "available" &&
                book.progress_percentage !== undefined &&
                book.progress_percentage !== null && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-primary-800">
                        Progress:
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          book.progress_percentage === 100
                            ? "text-primary-600"
                            : "text-primary-800"
                        }`}
                      >
                        {book.progress_percentage}%
                      </span>
                    </div>
                    <div className="w-full bg-primary-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          book.progress_percentage === 100
                            ? "bg-primary-600"
                            : "bg-primary-400"
                        }`}
                        style={{width: `${book.progress_percentage}%`}}
                      ></div>
                    </div>
                  </div>
                )}

              <div className="flex items-center gap-3">
                {book.amazon_url && (
                  <a
                    href={book.amazon_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-10 w-10 border-2 border-primary-200 bg-white hover:bg-primary-50"
                    aria-label="View on Amazon"
                    title="View on Amazon"
                  >
                    <FaAmazon className="h-5 w-5 text-primary-600" />
                  </a>
                )}

                {(book.status?.toLowerCase() === "available" ||
                  book.status?.toLowerCase() === "preorder") && (
                  <Link
                    to="/shop"
                    className="flex-1 btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                  >
                    {book.status?.toLowerCase() === "preorder"
                      ? "Preorder Now"
                      : "Buy Now"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Book modal removed (inline CTA used instead)

export default Projects;
